/**
 * abbreviations.js — Team and bookmaker abbreviation utility.
 *
 * Strategy (in order):
 *  1. TEAM_OVERRIDES: authoritative official league abbreviations.
 *  2. Normalize input (trim, collapse whitespace, strip diacritics via NFD).
 *  3. Case-insensitive TEAM_OVERRIDES lookup (handles API casing variance).
 *  4. Algorithmic fallback:
 *       a. Remove stopwords (of, at, the, …).
 *       b. Multi-word (≥2 remaining tokens): first letter of each → uppercase.
 *       c. Single word: first 3 letters → uppercase.
 *
 * Collision notes:
 *  - LAC is used for both NBA Clippers and NFL Chargers: different sports, no UI collision.
 *  - PHI, MIA, MIN, etc. are re-used across leagues by their real abbreviations.
 *  - "Athletics" (no city) → OAK fallback entry.
 */

// ── Official league abbreviation overrides ────────────────────────────────────
const TEAM_OVERRIDES = {
  // NBA
  "Atlanta Hawks":           "ATL",
  "Boston Celtics":          "BOS",
  "Brooklyn Nets":           "BKN",
  "Charlotte Hornets":       "CHA",
  "Chicago Bulls":           "CHI",
  "Cleveland Cavaliers":     "CLE",
  "Dallas Mavericks":        "DAL",
  "Denver Nuggets":          "DEN",
  "Detroit Pistons":         "DET",
  "Golden State Warriors":   "GSW",
  "Houston Rockets":         "HOU",
  "Indiana Pacers":          "IND",
  "LA Clippers":             "LAC",
  "Los Angeles Clippers":    "LAC",
  "Los Angeles Lakers":      "LAL",
  "Memphis Grizzlies":       "MEM",
  "Miami Heat":              "MIA",
  "Milwaukee Bucks":         "MIL",
  "Minnesota Timberwolves":  "MIN",
  "New Orleans Pelicans":    "NOP",
  "New York Knicks":         "NYK",
  "Oklahoma City Thunder":   "OKC",
  "Orlando Magic":           "ORL",
  "Philadelphia 76ers":      "PHI",
  "Phoenix Suns":            "PHX",
  "Portland Trail Blazers":  "POR",
  "Sacramento Kings":        "SAC",
  "San Antonio Spurs":       "SAS",
  "Toronto Raptors":         "TOR",
  "Utah Jazz":               "UTA",
  "Washington Wizards":      "WAS",

  // NFL
  "Arizona Cardinals":       "ARI",
  "Atlanta Falcons":         "ATL",
  "Baltimore Ravens":        "BAL",
  "Buffalo Bills":           "BUF",
  "Carolina Panthers":       "CAR",
  "Chicago Bears":           "CHI",
  "Cincinnati Bengals":      "CIN",
  "Cleveland Browns":        "CLE",
  "Dallas Cowboys":          "DAL",
  "Denver Broncos":          "DEN",
  "Detroit Lions":           "DET",
  "Green Bay Packers":       "GB",
  "Houston Texans":          "HOU",
  "Indianapolis Colts":      "IND",
  "Jacksonville Jaguars":    "JAX",
  "Kansas City Chiefs":      "KC",
  "Las Vegas Raiders":       "LV",
  "Los Angeles Chargers":    "LAC",
  "Los Angeles Rams":        "LAR",
  "Miami Dolphins":          "MIA",
  "Minnesota Vikings":       "MIN",
  "New England Patriots":    "NE",
  "New Orleans Saints":      "NO",
  "New York Giants":         "NYG",
  "New York Jets":           "NYJ",
  "Philadelphia Eagles":     "PHI",
  "Pittsburgh Steelers":     "PIT",
  "San Francisco 49ers":     "SF",
  "Seattle Seahawks":        "SEA",
  "Tampa Bay Buccaneers":    "TB",
  "Tennessee Titans":        "TEN",
  "Washington Commanders":   "WAS",

  // MLB
  "Arizona Diamondbacks":    "ARI",
  "Atlanta Braves":          "ATL",
  "Baltimore Orioles":       "BAL",
  "Boston Red Sox":          "BOS",
  "Chicago Cubs":            "CHC",
  "Chicago White Sox":       "CWS",
  "Cincinnati Reds":         "CIN",
  "Cleveland Guardians":     "CLE",
  "Colorado Rockies":        "COL",
  "Houston Astros":          "HOU",
  "Kansas City Royals":      "KC",
  "Los Angeles Angels":      "LAA",
  "Los Angeles Dodgers":     "LAD",
  "Miami Marlins":           "MIA",
  "Milwaukee Brewers":       "MIL",
  "Minnesota Twins":         "MIN",
  "New York Mets":           "NYM",
  "New York Yankees":        "NYY",
  "Oakland Athletics":       "OAK",
  "Athletics":               "OAK",
  "Philadelphia Phillies":   "PHI",
  "Pittsburgh Pirates":      "PIT",
  "San Diego Padres":        "SD",
  "San Francisco Giants":    "SF",
  "Seattle Mariners":        "SEA",
  "St. Louis Cardinals":     "STL",
  "Tampa Bay Rays":          "TB",
  "Texas Rangers":           "TEX",
  "Toronto Blue Jays":       "TOR",
  "Detroit Tigers":          "DET",
  "Washington Nationals":    "WSH",

  // NHL
  "Anaheim Ducks":           "ANA",
  "Arizona Coyotes":         "ARI",
  "Boston Bruins":           "BOS",
  "Buffalo Sabres":          "BUF",
  "Calgary Flames":          "CGY",
  "Carolina Hurricanes":     "CAR",
  "Chicago Blackhawks":      "CHI",
  "Colorado Avalanche":      "COL",
  "Columbus Blue Jackets":   "CBJ",
  "Dallas Stars":            "DAL",
  "Detroit Red Wings":       "DET",
  "Edmonton Oilers":         "EDM",
  "Florida Panthers":        "FLA",
  "Los Angeles Kings":       "LAK",
  "Minnesota Wild":          "MIN",
  "Montreal Canadiens":      "MTL",
  "Montréal Canadiens":      "MTL",
  "Nashville Predators":     "NSH",
  "New Jersey Devils":       "NJD",
  "New York Islanders":      "NYI",
  "New York Rangers":        "NYR",
  "Ottawa Senators":         "OTT",
  "Philadelphia Flyers":     "PHI",
  "Pittsburgh Penguins":     "PIT",
  "San Jose Sharks":         "SJS",
  "Seattle Kraken":          "SEA",
  "St. Louis Blues":         "STL",
  "St Louis Blues":          "STL",
  "Tampa Bay Lightning":     "TBL",
  "Toronto Maple Leafs":     "TOR",
  "Vancouver Canucks":       "VAN",
  "Vegas Golden Knights":    "VGK",
  "Washington Capitals":     "WSH",
  "Winnipeg Jets":           "WPG",
  "Utah Mammoth":            "UTA",
};

// ── Bookmaker short display labels ────────────────────────────────────────────
const BOOKMAKER_SHORTS = {
  "DraftKings":        "DK",
  "FanDuel":           "FD",
  "BetMGM":            "MGM",
  "Caesars":           "CZS",
  "Hard Rock Bet":     "HRB",
  "Hard Rock Bet AZ":  "HRB",
  "Hard Rock Bet FL":  "HRB",
  "Hard Rock Bet OH":  "HRB",
  "ESPN BET":          "ESPN",
  "theScore Bet":      "ESPN",
  "BetRivers":         "BR",
  "Fanatics":          "FAN",
  "Bally Bet":         "BAL",
  "BetParx":           "PARX",
  "BetUS":             "BUS",
  "Bovada":            "BOV",
  "LowVig.ag":         "LVG",
  "Novig":             "NVG",
  "ReBet":             "RBT",
  "Betr DFS":          "BTR",
  "DraftKings Pick6":  "PK6",
  "PrizePicks":        "PP",
  "Underdog":          "UD",
  "Kalshi":            "KAL",
};

// ── Stopwords to skip in algorithmic abbreviation ─────────────────────────────
const STOPWORDS = new Set([
  "at", "the", "of", "in", "and", "or", "de", "los", "las", "le", "la",
]);

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Normalize a raw string: trim, collapse whitespace, strip diacritics (NFD).
 * @param {string} raw
 * @returns {string}
 */
function normalizeName(raw) {
  return String(raw ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Algorithmic abbreviation fallback.
 *  - Remove stopwords and non-alphanumeric tokens.
 *  - 2+ remaining tokens: first letter of each → uppercase (e.g. "NYK").
 *  - 1 token: first 3 letters → uppercase (e.g. "CEL").
 *  - 0 tokens (edge case): first 3 chars of original.
 * @param {string} name  Already-normalized name
 * @returns {string}
 */
function algorithmicAbbr(name) {
  const tokens = name
    .split(/[\s\-_.]+/)
    .map(t => t.replace(/[^a-zA-Z0-9]/g, ""))
    .filter(t => t.length > 0 && !STOPWORDS.has(t.toLowerCase()));

  if (tokens.length === 0) return name.slice(0, 3).toUpperCase();
  if (tokens.length === 1) return tokens[0].slice(0, 3).toUpperCase();
  return tokens.map(t => t[0]).join("").toUpperCase();
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Get the official or computed abbreviation for a team name.
 *
 * @param {string} name  Full team name (e.g. "Los Angeles Lakers")
 * @returns {string}     2–4 character abbreviation (e.g. "LAL")
 */
export function getAbbreviation(name) {
  if (!name) return "?";
  const norm = normalizeName(name);

  // Direct lookup (exact, case-sensitive)
  if (TEAM_OVERRIDES[norm]) return TEAM_OVERRIDES[norm];

  // Case-insensitive lookup (handles API casing variance)
  const lower = norm.toLowerCase();
  const foundKey = Object.keys(TEAM_OVERRIDES).find(
    k => k.toLowerCase() === lower
  );
  if (foundKey) return TEAM_OVERRIDES[foundKey];

  // Algorithmic fallback
  return algorithmicAbbr(norm);
}

/**
 * Get a short display label for a bookmaker name.
 * Falls back to the first 4 alphanumeric chars uppercased.
 *
 * @param {string} name  Bookmaker display name (e.g. "DraftKings")
 * @returns {string}     Short label (e.g. "DK")
 */
export function shortBook(name) {
  if (!name) return "–";
  if (BOOKMAKER_SHORTS[name]) return BOOKMAKER_SHORTS[name];

  // Case-insensitive lookup
  const lower = name.toLowerCase();
  const foundKey = Object.keys(BOOKMAKER_SHORTS).find(
    k => k.toLowerCase() === lower
  );
  if (foundKey) return BOOKMAKER_SHORTS[foundKey];

  // Fallback: first 4 alphanumeric chars
  return name.replace(/\s+/g, "").slice(0, 4).toUpperCase();
}
